import { brand } from "../../app/brand.js";
import { CERTIFICATION_STATUS } from "./reportCatalog.js";
import { formatMetricValue, type ReportDossier, type ReportMetric } from "./reportDossier.js";

const FOLIO_MASTHEAD = "Provenance · evidence folio";

function MetricTable({ items, empty }: { items: readonly ReportMetric[]; empty: string }) {
  if (items.length === 0) return <p>{empty}</p>;
  return (
    <table>
      <thead>
        <tr>
          <th>Metric</th>
          <th>Value</th>
          <th>Limitation</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item) => (
          <tr key={item.id}>
            <th scope="row">{item.label}</th>
            <td>{formatMetricValue(item.value)}</td>
            <td>{item.limitation ?? "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ReportFolio({ dossier }: { dossier: ReportDossier }) {
  const { format, evidence } = dossier;
  return (
    <article className="a4-folio" aria-label={`${format.name} A4 evidence folio`}>
      <header>
        <div>
          <p>
            {FOLIO_MASTHEAD} · ISO 216 A4
          </p>
          <h2>{format.name}</h2>
          <p>{format.edition}</p>
        </div>
        <p className="a4-folio__stamp">{dossier.stamp}</p>
      </header>
      <section>
        <h3>Scope</h3>
        <dl>
          <div>
            <dt>Tenant</dt>
            <dd>{evidence.tenantId}</dd>
          </div>
          <div>
            <dt>Scope</dt>
            <dd>{evidence.scopeLabel}</dd>
          </div>
          <div>
            <dt>Scan</dt>
            <dd>{evidence.scanId ?? "latest per repository"}</dd>
          </div>
          <div>
            <dt>Generated</dt>
            <dd>{dossier.generatedAt}</dd>
          </div>
          <div>
            <dt>Certification status</dt>
            <dd>{CERTIFICATION_STATUS}</dd>
          </div>
        </dl>
      </section>
      <section>
        <h3>Admitted metrics</h3>
        {evidence.loading ? (
          <p>Loading admitted metrics. Unavailable values will not be replaced with zero.</p>
        ) : (
          <MetricTable
            items={evidence.metrics}
            empty="No verified metrics were returned for this scope."
          />
        )}
      </section>
      <section>
        <h3>Decision funnel</h3>
        <MetricTable
          items={evidence.funnel.map((stage) => ({
            id: stage.id,
            label: stage.label,
            value: stage.value,
            limitation: null,
          }))}
          empty="No funnel stages were returned."
        />
      </section>
      <section>
        <h3>Reconciliation</h3>
        <MetricTable
          items={evidence.reconciliation}
          empty="No reconciliation counts were returned."
        />
      </section>
      <section>
        <h3>Evidence offered to an assessor</h3>
        {evidence.slots.length === 0 ? (
          <p>No evidence slots were derived.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Slot</th>
                <th>State</th>
                <th>Observation</th>
              </tr>
            </thead>
            <tbody>
              {evidence.slots.map((slot) => (
                <tr key={slot.id}>
                  <th scope="row">{slot.label}</th>
                  <td>{slot.state === "observed" ? "Observed" : "Not assessed"}</td>
                  <td>{slot.observation}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      {format.id === "pqc_inventory" ? (
        <section>
          <h3>Observed cryptography inventory</h3>
          <p>
            Ordinary hashing is not treated as a finding. Retention horizon is unknown from static
            source.
          </p>
          <dl>
            <div>
              <dt>Inventory available</dt>
              <dd>{evidence.pqc.available ? "Yes" : "No"}</dd>
            </div>
            <div>
              <dt>Observed uses</dt>
              <dd>{formatMetricValue(evidence.pqc.total)}</dd>
            </div>
            <div>
              <dt>Ordinary hashing observations</dt>
              <dd>{formatMetricValue(evidence.pqc.hashingObservationCount)}</dd>
            </div>
          </dl>
          {evidence.pqc.classifications.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Class</th>
                  <th>Count</th>
                  <th>Finding-linked</th>
                </tr>
              </thead>
              <tbody>
                {evidence.pqc.classifications.map((row) => (
                  <tr key={row.value}>
                    <th scope="row">{row.value}</th>
                    <td>{row.count}</td>
                    <td>{row.findingLinkedCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p>No algorithm-class rows were returned.</p>
          )}
          <p>{evidence.pqc.limitation}</p>
        </section>
      ) : (
        <section>
          <h3>Supply-chain evidence offered to an assessor</h3>
          <dl>
            <div>
              <dt>SBOM available</dt>
              <dd>{evidence.sbom.available ? "Yes" : "No"}</dd>
            </div>
            <div>
              <dt>SBOM components</dt>
              <dd>{formatMetricValue(evidence.sbom.uniqueComponents ?? evidence.sbom.total)}</dd>
            </div>
            <div>
              <dt>PQC inventory uses</dt>
              <dd>{formatMetricValue(evidence.pqc.total)}</dd>
            </div>
          </dl>
          <p>{evidence.sbom.limitation}</p>
        </section>
      )}
      <section>
        <h3>Limitations</h3>
        <ul>
          {[...new Set([...evidence.limitations, dossier.stamp])].map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>
      <footer>
        Recreatable from this scan. Empty or unavailable values are not substituted with zero.{" "}
        {format.focus}
      </footer>
    </article>
  );
}
